import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { api } from '../../lib/api'

const schema = z
  .object({
    tenantName: z.string().min(2, 'Минимум 2 символа'),
    ownerName: z.string().min(2, 'Минимум 2 символа'),
    ownerPhone: z.string().min(10, 'Введите номер телефона'),
    ownerPassword: z.string().min(6, 'Минимум 6 символов'),
    confirmPassword: z.string().min(6, 'Минимум 6 символов'),
    inviteCode: z.string().optional(),
  })
  .refine((d) => d.ownerPassword === d.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  })

type FormData = z.infer<typeof schema>

export default function RegisterPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [requiresInviteCode, setRequiresInviteCode] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) })

  useEffect(() => {
    api.get('/tenants/register/info').then((r) => {
      setRequiresInviteCode(!!r.data.requiresInviteCode)
    }).catch(() => {})
  }, [])

  const onSubmit = async (data: FormData) => {
    setError(null)
    try {
      await api.post('/tenants/register', {
        tenantName: data.tenantName,
        ownerName: data.ownerName,
        ownerPhone: data.ownerPhone,
        ownerPassword: data.ownerPassword,
        inviteCode: data.inviteCode,
      })
      setSuccess(true)
      setTimeout(() => navigate('/login'), 2500)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message
      setError(msg ?? 'Ошибка регистрации')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10 flex flex-col items-center">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <span className="text-5xl">🚲</span>
          <h1 className="mt-3 text-2xl font-bold text-gray-900">VeloRent</h1>
          <p className="mt-1 text-sm text-gray-500">Регистрация новой точки проката</p>
        </div>

        {success ? (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 text-center">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-semibold text-green-800">Аккаунт создан!</p>
            <p className="text-sm text-green-600 mt-1">Перенаправляем на вход...</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Название проката</label>
                <input
                  {...register('tenantName')}
                  type="text"
                  placeholder="ВелоПрокат Центр"
                  className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                />
                {errors.tenantName && <p className="mt-1 text-xs text-red-600">{errors.tenantName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ваше имя (владелец)</label>
                <input
                  {...register('ownerName')}
                  type="text"
                  placeholder="Иван Иванов"
                  className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                />
                {errors.ownerName && <p className="mt-1 text-xs text-red-600">{errors.ownerName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Телефон</label>
                <input
                  {...register('ownerPhone')}
                  type="tel"
                  placeholder="+7 900 000 00 00"
                  className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                />
                {errors.ownerPhone && <p className="mt-1 text-xs text-red-600">{errors.ownerPhone.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Пароль</label>
                <input
                  {...register('ownerPassword')}
                  type="password"
                  placeholder="••••••"
                  className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                />
                {errors.ownerPassword && <p className="mt-1 text-xs text-red-600">{errors.ownerPassword.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Повторите пароль</label>
                <input
                  {...register('confirmPassword')}
                  type="password"
                  placeholder="••••••"
                  className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                />
                {errors.confirmPassword && <p className="mt-1 text-xs text-red-600">{errors.confirmPassword.message}</p>}
              </div>

              {requiresInviteCode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Код приглашения <span className="text-red-500">*</span>
                  </label>
                  <input
                    {...register('inviteCode')}
                    type="text"
                    placeholder="DEMO-XXXX"
                    className="w-full rounded-xl border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 text-base"
                  />
                  <p className="mt-1 text-xs text-gray-400">Запросите код у менеджера VeloRent</p>
                  {errors.inviteCode && <p className="mt-1 text-xs text-red-600">{errors.inviteCode.message}</p>}
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-brand-600 hover:bg-brand-700 disabled:opacity-60 text-white font-semibold py-3 px-4 rounded-xl transition-colors text-base"
              >
                {isSubmitting ? 'Создание...' : 'Зарегистрироваться'}
              </button>
            </form>

            <p className="mt-4 text-center text-sm text-gray-500">
              Уже есть аккаунт?{' '}
              <Link to="/login" className="text-brand-600 font-medium hover:underline">
                Войти
              </Link>
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
